"use client";

import React, { useEffect, useState, useRef } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import {
  Home,
  Wallet,
  Plus,
  Send,
  HelpCircle,
  Code2,
  Menu,
  X,
  User,
  LogOut,
  ShieldCheck,
  GitPullRequest,
  ChartNetworkIcon,
  ArrowLeftFromLine,
  HandCoins,
  TrendingUp,
  Camera,
  Loader2,
  HeadphonesIcon,
  Lock,
  ScrollText,
  ChevronRight,
  BookOpen,
  Settings2,
} from "lucide-react";
import { useAuth, useWallet } from "@crossmint/client-sdk-react-ui";
import { db, storage } from "../../firebase";
import { doc, onSnapshot, updateDoc, collection, query, where } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import toast from "react-hot-toast";
import { useWebhookSummary } from "./developers/webhooks/useWebhookSummary";

// Webhook-alert count bubble for the Developers nav group: red for a
// critical alert (endpoint disabled / deliveries failed permanently), amber
// for a warning (endpoint failing, still retrying). role="img" makes screen
// readers announce the aria-label instead of a bare number.
function WebhookNavBadge({ count, tone }: { count: number; tone: "red" | "amber" }) {
  const label = `${count} webhook ${count === 1 ? "endpoint needs" : "endpoints need"} attention`;
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={`ml-auto min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full text-[9px] font-bold shrink-0 ${
        tone === "red" ? "bg-red-500 text-white" : "bg-amber-500 text-black"
      }`}
    >
      {count > 9 ? "9+" : count}
    </span>
  );
}

// Blue "New" pill — the same treatment the Collect entry uses. Shared by the
// Developers parent and its Webhooks child so the markup stays in one place.
function NewPill({ className = "" }: { className?: string }) {
  return (
    <span
      className={`px-1.5 py-0.5 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[9px] font-bold tracking-wider uppercase shadow-sm shadow-blue-500/40 shrink-0 ${className}`}
    >
      New
    </span>
  );
}

// "NEW" badge on the Developers nav entry — visible for 10 days from
// the launch date so business users have a clear "go look at this"
// signal without us needing to push a follow-up to hide it. After the
// expiry the badge auto-suppresses; no deploy required.
//
// Today is 2026-06-05 → badge stays up until 2026-06-15 UTC.
const DEVELOPERS_NEW_BADGE_EXPIRES_AT = Date.UTC(2026, 5 /* June */, 15);

// Webhook delivery shipped 2026-09-20 → "New" on Developers and on its
// Webhooks child until 2026-12-20 UTC, after which both auto-suppress.
const WEBHOOKS_NEW_BADGE_EXPIRES_AT = Date.UTC(2026, 11 /* December */, 20);

export function Layout() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [developersOpen, setDevelopersOpen] = useState(() =>
    typeof window !== "undefined" && window.location.pathname.startsWith("/developers"),
  );
  // Computed once per render — cheap, deterministic, and lets the badge
  // disappear naturally as soon as the page is loaded after the expiry.
  const showDevelopersNewBadge = Date.now() < DEVELOPERS_NEW_BADGE_EXPIRES_AT;
  const showWebhooksNewBadge = Date.now() < WEBHOOKS_NEW_BADGE_EXPIRES_AT;
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [smoothPosition, setSmoothPosition] = useState({ x: 0, y: 0 });
  const { user, logout, status: authStatus } = useAuth();
  const { wallet } = useWallet();

  const authenticated = authStatus === "logged-in";
  const userEmail = user?.email || null;
  const walletAddress = wallet?.address?.toLowerCase() || null;

  const [profilePhotoURL, setProfilePhotoURL] = useState<string | null>(null);
  const [isBusiness, setIsBusiness] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);

  // Webhook alerts for the Developers badge. Only business accounts poll —
  // a null wallet keeps the shared summary poller idle for everyone else.
  const { summary: webhookSummary } = useWebhookSummary(
    authenticated && isBusiness ? walletAddress : null,
    userEmail,
  );
  const webhookBadgeCount =
    webhookSummary?.merchant && webhookSummary.alertLevel !== "none" ? webhookSummary.badgeCount : 0;
  const webhookBadgeTone: "red" | "amber" =
    webhookSummary?.alertLevel === "critical" ? "red" : "amber";

  const truncateAddress = (address: string | null) => {
    if (!address) return "Guest";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getInitials = (email: string | null) => {
    if (!email) return "??";
    const parts = email.split("@")[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return (email[0] + (email[1] || "")).toUpperCase();
  };

  const lerp = (start: number, end: number, factor: number) => {
    return start + (end - start) * factor;
  };

  useEffect(() => {
    if (!authenticated || !walletAddress) {
      setProfilePhotoURL(null);
      return;
    }

    const userRef = doc(db, "users", walletAddress);

    const unsubscribe = onSnapshot(
      userRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProfilePhotoURL(data.photoURL || null);
          setIsBusiness(data.isBusiness || false);
        } else {
          setProfilePhotoURL(null);
          setIsBusiness(false);
        }
      },
      (error) => {
        console.error("Error fetching user profile photo:", error);
        toast.error("Failed to load profile picture");
      },
    );

    return () => unsubscribe();
  }, [authenticated, walletAddress]);

  // Listen for unread support messages
  useEffect(() => {
    if (!authenticated || !walletAddress) {
      setSupportUnreadCount(0);
      return;
    }

    const q = query(
      collection(db, "supportThreads"),
      where("walletAddress", "==", walletAddress)
    );

    const unsub = onSnapshot(q, (snap) => {
      let count = 0;
      snap.docs.forEach((d) => {
        const data = d.data();
        if (data.unreadByUser > 0) count += data.unreadByUser;
      });
      setSupportUnreadCount(count);
    });

    return () => unsub();
  }, [authenticated, walletAddress]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);

    let animationFrameId: number | null = null;
    const updateSmoothPosition = () => {
      setSmoothPosition((prev) => ({
        x: lerp(prev.x, cursorPosition.x, 0.1),
        y: lerp(prev.y, cursorPosition.y, 0.1),
      }));
      animationFrameId = requestAnimationFrame(updateSmoothPosition);
    };
    animationFrameId = requestAnimationFrame(updateSmoothPosition);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [cursorPosition]);

  const handleProfilePhotoChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !walletAddress) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setUploading(true);

    try {
      const storageRef = ref(storage, `profile-pictures/${walletAddress}.jpg`);

      await uploadBytes(storageRef, file, { contentType: file.type });

      const downloadURL = await getDownloadURL(storageRef);

      const userRef = doc(db, "users", walletAddress);
      await updateDoc(userRef, {
        photoURL: downloadURL,
        updatedAt: new Date(),
      });

      toast.success("Profile picture updated!");
    } catch (err) {
      console.error("Profile photo upload failed:", err);
      toast.error("Failed to upload profile picture. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const navigationItems = [
   
    { path: "/dashboard", icon: Home, label: "Home" },
    { path: "/wallet", icon: Wallet, label: "Wallet" },
    { path: "/add-funds", icon: Plus, label: "Add Money" },
    { path: "/collect", icon: HandCoins, label: "Collect", newUntil: "2027-06-24" },
    { path: "/earn", icon: TrendingUp, label: "Earn" },
    { path: "/transfer", icon: Send, label: "Send" },
    { path: "/analytics", icon: ChartNetworkIcon, label: "Analytics" },
    {
      path: "/paymentRequests",
      icon: GitPullRequest,
      label: "Payment Requests",
    },
    { path: "/support", icon: HeadphonesIcon, label: "Support", badge: supportUnreadCount },
    { path: "/faqs", icon: HelpCircle, label: "FAQs" },
  ];

  // Developers sub-items — kept separate so the collapsible group can be
  // rendered inline without fitting awkwardly into the flat navigationItems array.
  // Icons are real lucide components (not emoji) so they pick up the same
  // visual treatment as the parent nav items: monoline, currentColor-tinted,
  // pixel-aligned on hover/active states. The previous 📄 emoji rendered
  // platform-dependent and didn't visually match the rest of the sidebar.
  const developerChildren: {
    path: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    badge?: number;
    badgeTone?: "red" | "amber";
    isNew?: boolean;
  }[] = [
    { path: "/developers/api-settings", label: "API Settings", icon: Settings2 },
    { path: "/developers/webhooks",     label: "Webhooks",     icon: Send, badge: webhookBadgeCount, badgeTone: webhookBadgeTone, isNew: showWebhooksNewBadge },
    { path: "/developers/docs",         label: "Documentation", icon: BookOpen },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMobileMenuOpen &&
        !(event.target as HTMLElement)?.closest(".mobile-menu-container")
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileMenuOpen]);

  return (
    <div className="bg-gradient-to-br scrollbar-hide from-[#090D14] via-[#0A0E15] to-[#0B0F16] text-white h-screen flex flex-col overflow-hidden">
      {/* Custom cursor */}
      <div
        className="fixed w-3 h-3 bg-[#61CE70] rounded-full pointer-events-none transition-transform duration-300 ease-out shadow-lg shadow-[#61CE70]/30 hidden lg:block z-[9999]"
        style={{
          transform: `translate(${smoothPosition.x - 6}px, ${smoothPosition.y - 6}px)`,
        }}
      />

      {/* Header */}
      <header className="bg-[#0F1419]/80 backdrop-blur-xl z-[1000] border-b border-gray-800/50 px-4 sm:px-6 sticky top-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-14">
          <div className="flex items-center">
            <img
              src="/logo.png"
              alt="Logo"
              className="w-20 h-10 sm:w-24 sm:h-11 object-contain"
            />
          </div>

          <div className="flex items-center space-x-3">
            {authenticated && (
              <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-[#61CE70]/30">
                {profilePhotoURL ? (
                  <img
                    src={profilePhotoURL}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#61CE70] to-[#50B060] flex items-center justify-center text-white font-bold text-xs">
                    {getInitials(userEmail)}
                  </div>
                )}
              </div>
            )}

            <div className="hidden md:flex items-center space-x-2">
              <button className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300 hidden lg:block max-w-[160px] truncate">
                  {authenticated
                    ? userEmail || truncateAddress(walletAddress)
                    : "Guest"}
                </span>
                {authenticated && isBusiness && (
                  <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/25 rounded-full inline-flex items-center gap-0.5">
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                    Business
                  </span>
                )}
              </button>

              {authenticated && (
                <button
                  onClick={() => logout()}
                  className="flex items-center space-x-1.5 px-3 py-2 rounded-lg bg-red-500/80 text-white text-sm font-medium transition-all hover:bg-red-600"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden lg:block">Log Out</span>
                </button>
              )}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-all active:scale-95"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 relative overflow-hidden">
        {/* FIXED SIDEBAR */}
        <aside
          className={`
            hidden md:block fixed inset-y-0 left-0 z-[900]
            w-56 lg:w-60
            bg-[#0F1419]/50 backdrop-blur-xl
            border-r border-gray-800/30
            overflow-y-auto scrollbar-hide
            pt-14
          `}
        >
          <div className="flex flex-col min-h-full px-3 py-4">
            <ul className="space-y-1 flex-grow">
              {navigationItems.map((item) => {
                const { path, icon: Icon, label, external, badge, newUntil } = item as any;
                const showNew = !!newUntil && new Date() < new Date(newUntil);
                const isActive = location.pathname === path;
                return (
                  <li key={path}>
                    {external ? (
                      <a
                        href={path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm bg-gray-800/40 text-gray-300 hover:bg-[#61CE70]/10 hover:text-white"
                      >
                        <Icon className="w-5 h-5 shrink-0 group-hover:text-[#61CE70] transition-colors" />
                        <span className="font-medium truncate">{label}</span>
                      </a>
                    ) : (
                      <Link
                        to={path}
                        className={`group flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm ${
                          isActive
                            ? "bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white shadow-md shadow-[#61CE70]/15"
                            : "text-gray-400 hover:bg-gray-800/50 hover:text-white"
                        }`}
                      >
                        <div className="relative shrink-0">
                          <Icon className="w-5 h-5" />
                          {badge && badge > 0 ? (
                            <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-0.5">
                              {badge > 99 ? "99+" : badge}
                            </span>
                          ) : null}
                        </div>
                        <span className="font-medium truncate">{label}</span>
                        {showNew && (
                          <span className="ml-auto px-1.5 py-0.5 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[9px] font-bold tracking-wider uppercase shadow-sm shadow-blue-500/40">
                            New
                          </span>
                        )}
                      </Link>
                    )}
                  </li>
                );
              })}

              {/* ── Developers collapsible group (business users only) ── */}
              {isBusiness && (
                <li>
                  <button
                    type="button"
                    onClick={() => setDevelopersOpen((o) => !o)}
                    className={`w-full group flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm ${
                      location.pathname.startsWith("/developers")
                        ? "bg-[#61CE70]/10 text-white border border-[#61CE70]/20"
                        : "text-gray-400 hover:bg-gray-800/50 hover:text-white"
                    }`}
                  >
                    <Code2 className="w-5 h-5 shrink-0" />
                    <span className="font-medium truncate flex-1 text-left">Developers</span>
                    {/* Collapsed: the webhook badge sits on the parent (open: on the Webhooks child). */}
                    {!developersOpen && webhookBadgeCount > 0 && (
                      <WebhookNavBadge count={webhookBadgeCount} tone={webhookBadgeTone} />
                    )}
                    {(showDevelopersNewBadge || showWebhooksNewBadge) && webhookBadgeCount === 0 && (
                      // Same treatment as the Collect "New" badge. The failure
                      // badge wins when an endpoint needs attention.
                      <NewPill />
                    )}
                    <ChevronRight
                      className={`w-4 h-4 shrink-0 transition-transform duration-200 ${developersOpen ? "rotate-90" : ""}`}
                    />
                  </button>

                  {/* Animated children */}
                  <div
                    style={{ maxHeight: developersOpen ? "168px" : "0", overflow: "hidden", transition: "max-height 220ms ease" }}
                  >
                    <ul className="mt-1 ml-3 pl-3 border-l border-[#61CE70]/20 space-y-0.5 pb-1">
                      {developerChildren.map((child) => {
                        const childActive =
                          location.pathname === child.path || location.pathname.startsWith(child.path + "/");
                        const ChildIcon = child.icon;
                        return (
                          <li key={child.path}>
                            <Link
                              to={child.path}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] transition-colors ${
                                childActive
                                  ? "bg-[#61CE70]/15 text-[#61CE70] font-medium"
                                  : "text-gray-500 hover:bg-gray-800/40 hover:text-gray-200"
                              }`}
                            >
                              <ChildIcon className={`w-3.5 h-3.5 shrink-0 ${childActive ? "" : "opacity-70"}`} />
                              {child.label}
                              {developersOpen && child.badge && child.badge > 0 ? (
                                <WebhookNavBadge count={child.badge} tone={child.badgeTone || "red"} />
                              ) : child.isNew ? (
                                <NewPill className="ml-auto" />
                              ) : null}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </li>
              )}
            </ul>

            {authenticated && (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="mt-auto pt-4 border-t border-gray-800/50 cursor-pointer"
              >
                <div className="space-y-3 p-3 bg-gray-800/30 rounded-xl">
                  <div className="relative group mx-auto w-12 h-12">
                    {profilePhotoURL ? (
                      <img
                        src={profilePhotoURL}
                        alt="Profile"
                        className="w-full h-full object-cover rounded-full border-2 border-[#61CE70]/50"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-[#61CE70] to-[#50B060] flex items-center justify-center text-white font-bold text-lg">
                        {getInitials(userEmail)}
                      </div>
                    )}

                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="absolute -bottom-0.5 -right-0.5 p-1 bg-[#61CE70] text-black rounded-full hover:bg-[#50B060] transition-colors disabled:opacity-50"
                      title="Change profile picture"
                    >
                      {uploading ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Camera className="w-3 h-3" />
                      )}
                    </button>

                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleProfilePhotoChange}
                      className="hidden"
                    />
                  </div>

                  <p className="font-medium truncate text-sm text-center">
                    {userEmail?.split("@")[0] ||
                      truncateAddress(walletAddress)}
                  </p>

                  <button
                    onClick={() => logout()}
                    className="flex items-center justify-center space-x-1.5 w-full bg-red-500/80 hover:bg-red-600 text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Log Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* MOBILE MENU DRAWER */}
        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm mobile-menu-container">
            <div className="fixed inset-y-0 left-0 w-4/5 max-w-[300px] bg-[#0F1419]/95 backdrop-blur-xl border-r border-gray-800/50 shadow-2xl overflow-y-auto">
              <div className="p-5">
                <div className="flex items-center justify-between mb-5">
                  <img
                    src="/logo.png"
                    alt="Logo"
                    className="w-20 h-10 object-contain"
                  />
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-6 p-4 bg-gray-800/40 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className="relative shrink-0 w-14 h-14">
                      {profilePhotoURL ? (
                        <img
                          src={profilePhotoURL}
                          alt="Profile"
                          className="w-full h-full object-cover rounded-full border-2 border-[#61CE70]/50"
                        />
                      ) : (
                        <div className="w-full h-full rounded-full bg-gradient-to-br from-[#61CE70] to-[#50B060] flex items-center justify-center text-white font-bold text-xl">
                          {getInitials(userEmail)}
                        </div>
                      )}

                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="absolute -bottom-1 -right-1 bg-[#61CE70] text-black p-1.5 rounded-full hover:bg-[#50B060] transition-colors disabled:opacity-50"
                        title="Change profile picture"
                      >
                        {uploading ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Camera className="w-3.5 h-3.5" />
                        )}
                      </button>

                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleProfilePhotoChange}
                        className="hidden"
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-base truncate">
                        {userEmail?.split("@")[0] ||
                          truncateAddress(walletAddress)}
                      </p>
                      <button
                        onClick={() => logout()}
                        className="mt-2 flex items-center space-x-1.5 text-sm text-red-400 hover:text-red-300 transition-colors"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        <span>Log Out</span>
                      </button>
                    </div>
                  </div>
                </div>

                <ul className="space-y-1">
                  {navigationItems.map((item) => {
                    const { path, icon: Icon, label, external, badge, newUntil } = item as any;
                    const showNew = !!newUntil && new Date() < new Date(newUntil);
                    const isActive = location.pathname === path;
                    return (
                      <li key={path}>
                        {external ? (
                          <a
                            href={path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 text-[15px] bg-gray-800/40 text-gray-300 hover:bg-[#61CE70]/10 hover:text-white"
                          >
                            <Icon className="w-5 h-5 shrink-0 group-hover:text-[#61CE70] transition-colors" />
                            <span className="font-medium">{label}</span>
                          </a>
                        ) : (
                          <Link
                            to={path}
                            className={`group flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 text-[15px] ${
                              isActive
                                ? "bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white shadow-md shadow-[#61CE70]/20"
                                : "text-gray-300 hover:bg-gray-800/60 hover:text-white"
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <div className="relative shrink-0">
                              <Icon className="w-5 h-5" />
                              {badge && badge > 0 ? (
                                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-0.5">
                                  {badge > 99 ? "99+" : badge}
                                </span>
                              ) : null}
                            </div>
                            <span className="font-medium">{label}</span>
                            {showNew && (
                              <span className="ml-auto px-1.5 py-0.5 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[9px] font-bold tracking-wider uppercase shadow-sm shadow-blue-500/40">
                                New
                              </span>
                            )}
                          </Link>
                        )}
                      </li>
                    );
                  })}

                  {/* ── Developers collapsible group (business users only) ── */}
                  {isBusiness && (
                    <li>
                      <button
                        type="button"
                        onClick={() => setDevelopersOpen((o) => !o)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 text-[15px] ${
                          location.pathname.startsWith("/developers")
                            ? "bg-[#61CE70]/10 text-white border border-[#61CE70]/20"
                            : "text-gray-300 hover:bg-gray-800/60 hover:text-white"
                        }`}
                      >
                        <Code2 className="w-5 h-5 shrink-0" />
                        <span className="font-medium flex-1 text-left">Developers</span>
                        {/* Collapsed: the webhook badge sits on the parent (open: on the Webhooks child). */}
                        {!developersOpen && webhookBadgeCount > 0 && (
                          <WebhookNavBadge count={webhookBadgeCount} tone={webhookBadgeTone} />
                        )}
                        {(showDevelopersNewBadge || showWebhooksNewBadge) && webhookBadgeCount === 0 && (
                          // Same blue gradient pill the Collect entry uses.
                          <NewPill />
                        )}
                        <ChevronRight
                          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${developersOpen ? "rotate-90" : ""}`}
                        />
                      </button>

                      <div
                        style={{ maxHeight: developersOpen ? "168px" : "0", overflow: "hidden", transition: "max-height 220ms ease" }}
                      >
                        <ul className="mt-1 ml-4 pl-3 border-l border-[#61CE70]/20 space-y-0.5 pb-1">
                          {developerChildren.map((child) => {
                            const childActive =
                              location.pathname === child.path || location.pathname.startsWith(child.path + "/");
                            const ChildIcon = child.icon;
                            return (
                              <li key={child.path}>
                                <Link
                                  to={child.path}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                    childActive
                                      ? "bg-[#61CE70]/15 text-[#61CE70] font-medium"
                                      : "text-gray-400 hover:bg-gray-800/40 hover:text-gray-200"
                                  }`}
                                >
                                  <ChildIcon className={`w-4 h-4 shrink-0 ${childActive ? "" : "opacity-70"}`} />
                                  {child.label}
                                  {developersOpen && child.badge && child.badge > 0 ? (
                                    <WebhookNavBadge count={child.badge} tone={child.badgeTone || "red"} />
                                  ) : child.isNew ? (
                                    <NewPill className="ml-auto" />
                                  ) : null}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* SCROLLABLE MAIN CONTENT */}
        <main
          className={`
            flex-1 ml-0 md:ml-56 lg:ml-60 min-w-0
            overflow-y-auto
            scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900/50
          `}
        >
          <div className="p-4 sm:p-6 md:p-8 lg:p-10 scrollbar-hide">
            <Outlet />
          </div>

          {location.pathname !== "/support" && <footer className="bg-gradient-to-b from-[#0F1419] to-[#0A0E14] border-t border-gray-800/60 py-10 px-6 mt-10">
            <div className="max-w-7xl mx-auto">
              {/* Policies row. Stacks on mobile, 3-up on md+. */}
              <div>
                <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-gray-500 mb-4">
                  <ScrollText className="w-3.5 h-3.5 text-[#61CE70]" />
                  Policies
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    {
                      to: "/aml-policy",
                      icon: ShieldCheck,
                      title: "AML & BSA Compliance",
                      subtitle: "Anti-money-laundering framework",
                    },
                    {
                      to: "/privacy-policy",
                      icon: Lock,
                      title: "Privacy & Data Policy",
                      subtitle: "How we handle your data",
                    },
                    {
                      to: "/terms-of-use",
                      icon: ScrollText,
                      title: "Terms of Use",
                      subtitle: "Customer & business terms",
                    },
                  ].map(({ to, icon: Icon, title, subtitle }) => (
                    <Link
                      key={to}
                      to={to}
                      className="group flex items-center gap-4 p-4 rounded-2xl border border-gray-800/60 bg-gray-900/30 hover:border-[#61CE70]/40 hover:bg-[#0F1419]/70 transition-all"
                    >
                      <span className="w-11 h-11 rounded-xl bg-[#61CE70]/10 border border-[#61CE70]/20 group-hover:bg-[#61CE70]/15 group-hover:border-[#61CE70]/30 flex items-center justify-center shrink-0 transition-colors">
                        <Icon className="w-5 h-5 text-[#61CE70]" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-gray-100 group-hover:text-white transition-colors leading-tight">
                          {title}
                        </span>
                        <span className="block text-[12px] text-gray-500 mt-1 leading-snug truncate">
                          {subtitle}
                        </span>
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#61CE70] group-hover:translate-x-0.5 transition-all shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-gray-800/40 text-center text-gray-600 text-xs">
                © {new Date().getFullYear()} CashTap
              </div>
            </div>
          </footer>}
        </main>
      </div>
    </div>
  );
}
